import { useContext, useState } from "react";
import "./login.scss";
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth } from "../../firebase";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import logo from "./final-logo-white.png";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../firebase";
import YubiKeyModal from "../../components/yubiKeyModal/YubiKeyModal";

const Login = () => {
  const [error, setError] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [yubiKey, setYubiKey] = useState("");
  const [showYubiModal, setShowYubiModal] = useState(false);
  const [yubiError, setYubiError] = useState("");
  const [pendingCredentials, setPendingCredentials] = useState(null);

  const navigate = useNavigate();
  const { dispatch } = useContext(AuthContext);

  const verifyYubiKey = (input) => {
    const expectedKey = "vJKdKHkrFcRkEHjLdicLEVlHNIdVIfbHHGEBcd";
    return input === expectedKey;
  };

  const handleYubiKeySubmit = () => {
    if (!verifyYubiKey(yubiKey)) {
      setYubiError("Invalid YubiKey input. Please try again.");
      return;
    }

    // Complete the login process with the pending credentials
    if (pendingCredentials) {
      dispatch({ type: "LOGIN", payload: pendingCredentials });
      navigate("/");
    }
  };
  
  // For development use only - skip YubiKey verification
  const handleDevSkip = () => {
    console.log("Development mode: Skipping YubiKey verification");
    if (pendingCredentials) {
      dispatch({ type: "LOGIN", payload: pendingCredentials });
      // Navigate directly to the home page, bypassing the loading screen
      navigate("/");
    }
  };

  const handleLogin = (e) => {
    e.preventDefault();
    setError(false);
    setYubiError("");
    
    // Create email by appending "@laddersuite.app" to the username
    const loginEmail = `${username}@laddersuite.app`;
    
    console.log("Login attempt for email:", loginEmail);

    // First try to authenticate with Firebase directly
    signInWithEmailAndPassword(auth, loginEmail, password)
      .then((userCredential) => {
        console.log("Firebase auth successful", userCredential.user.uid);
        
        // Now check if the user has the right permissions in Firestore
        const userQuery = query(
          collection(db, "PsapUsers"),
          where("email", "==", loginEmail)
        );
        
        return getDocs(userQuery).then((querySnapshot) => {
          console.log("Firestore query returned", querySnapshot.size, "results");
          
          if (!querySnapshot.empty) {
            const userDoc = querySnapshot.docs[0];
            const userData = userDoc.data();
            console.log("User data:", userData);

            if (userData.admin || userData.dispatch) {
              console.log("User has required permissions");
              return userCredential;
            } else {
              console.log("User lacks required permissions");
              throw new Error(
                "You don't have the required permissions to access this system. Contact support for more details."
              );
            }
          } else {
            console.log("No matching user document found in Firestore");
            throw new Error("User account not found in the system. Contact support for assistance.");
          }
        });
      })
      .then((userCredential) => {
        // Store the credentials and show YubiKey modal
        console.log("Showing YubiKey modal");
        setPendingCredentials(userCredential.user);
        setShowYubiModal(true);
      })
      .catch((error) => {
        console.error("Login error:", error);
        console.error("Error code:", error.code);
        console.error("Error message:", error.message);
        
        if (error.code === "auth/wrong-password" || error.code === "auth/user-not-found" || error.code === "auth/invalid-credential") {
          setError(true);
        } else {
          setError(true);
          // You might want to show the actual error message for other types of errors
          // alert(error.message);
        }
      });
  };

  const handleCloseYubiModal = () => {
    setShowYubiModal(false);
    setYubiKey("");
    setYubiError("");
    setPendingCredentials(null);
  };

  return (
    <div className="login-page">
      <div className="img-container">
        {/* Place your image or any other content here */}
        
      </div>
      <div className="login-container">
        <img src={logo} alt="Logo" className="login-logo" />
        <form onSubmit={handleLogin} className="login-form">
          <input
            type="text"
            placeholder="Username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            className="login-input"
          />
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="login-input"
          />
          <div className="login-remember">
            <input type="checkbox" id="rememberMe" />
            <label htmlFor="rememberMe">Remember me</label>
          </div>
          <button type="submit" className="login-button">
            LOGIN
          </button>
          {error && <span className="login-error">Wrong email or password!</span>}
        </form>
        <div className="warning">
          <span>* * * * * * * * W A R N I N G * * * * * * * *</span>
          <p>
            This system is the property of Ladder Suite LLC and is provided for
            official use by authorized government personnel and affiliated
            agencies only. Unauthorized access, use, or modification of this
            system is strictly prohibited and may result in disciplinary action,
            civil penalties, and/or criminal prosecution under applicable laws.
          </p>
          <p>
            All activity on this system is subject to monitoring, recording, and
            auditing. By proceeding, you expressly consent to such monitoring
            and acknowledge your obligation to comply with all applicable
            policies, including Ladder Suite’s Terms of Service and any
            governing agency regulations.
          </p>
          <p>
            If you are not an authorized user or do not agree to these
            conditions, <strong>log off immediately</strong>.
          </p>
        </div>
        <YubiKeyModal
          isOpen={showYubiModal}
          onClose={handleCloseYubiModal}
          onSubmit={handleYubiKeySubmit}
          onDevSkip={handleDevSkip}
          yubiKey={yubiKey}
          setYubiKey={setYubiKey}
          error={yubiError}
        />
      </div>
    </div>
  );
};

export default Login;
